/**
 * Chrome for the screens that render outside the app shell: sign-in, sign-up,
 * the magic-link callback, and invite accept.
 *
 * Each of these pages used to hardcode the same three inline styles on its own
 * `<main>`. `.auth-page main` (redesign-pages.css) already carries exactly
 * those values, so they are dropped here rather than repeated four times --
 * which also means the auth measure changes in CSS, once, instead of in four
 * components that can silently drift apart.
 */
import type { ReactNode } from "react";

import { Icon, type IconName } from "./Icon.tsx";

/**
 * The 52px rounded mark above a terminal auth message -- the handoff's
 * confirmation glyph on `operator-auth-callback.html` and
 * `operator-invite-accept.html`.
 *
 * `tone="warn"` swaps the mint plate for amber. There is deliberately no
 * "error" tone: these screens only ever report a link that didn't resolve,
 * which is recoverable by asking for a new one, and painting that in the
 * destructive colour would overstate it.
 */
export function AuthStatusMark({
  name,
  tone = "ok",
}: {
  name: IconName;
  tone?: "ok" | "warn";
}) {
  return (
    <span
      className={tone === "warn" ? "auth-status-mark is-warn" : "auth-status-mark"}
      aria-hidden="true"
    >
      <Icon name={name} />
    </span>
  );
}

export function AuthLayout({
  centered = false,
  className,
  children,
}: {
  /** Centre the column -- for the terminal states that are a mark, a
   *  headline and one action, not a form. */
  centered?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const classes = ["auth-page"];
  if (centered) classes.push("is-centered");
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")}>
      <main>{children}</main>
    </div>
  );
}
